const API_URL = process.env.REACT_APP_API_URL;

export const fetchCourses = async ({ page = 1, pageSize = 50, search = "" } = {}) => {
  const params = new URLSearchParams({ page, pageSize });
  if (search.trim()) params.append("search", search.trim());
  const res = await fetch(`${API_URL}/api/courses?${params}`);
  if (!res.ok) throw new Error(`HTTP error ${res.status}`);
  return res.json();
};

export const fetchCourseById = async (id) => {
  const res = await fetch(`${API_URL}/api/courses/${id}`);
  if (!res.ok) throw new Error(`HTTP error ${res.status}`);
  const json = await res.json();
  return json.data;
};

export const createCourse = async (payload) => {
  const res = await fetch(`${API_URL}/api/courses`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    // Keep the server's per-field detail — it says exactly what was rejected,
    // and the form uses it to mark the offending inputs. Throwing a flat
    // message here would leave the admin with nothing to act on.
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.message || "Failed to create course");
    error.fields = err.fields || null;
    throw error;
  }
  const json = await res.json();
  return json.data;
};

export const updateCourse = async (id, payload) => {
  const res = await fetch(`${API_URL}/api/courses/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    // Keep the per-field detail (e.g. a clashing slug) so the form can mark the
    // input, not just show a banner.
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.message || "Failed to update course");
    error.fields = err.fields || null;
    throw error;
  }
  return res.json();
};

export const deleteCourse = async (id) => {
  const res = await fetch(`${API_URL}/api/courses/${id}`, { method: "DELETE" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to delete course");
  }
  return res.json();
};

export const fetchAllSubjectsSimple = async () => {
  const res = await fetch(`${API_URL}/api/note-subjects/all`);
  if (!res.ok) throw new Error(`HTTP error ${res.status}`);
  return (await res.json()).data || [];
};

export const sendCourseNotification = async (courseId, type, title, body, attachments, whatsappConfig, emailConfig) => {
  const formData = new FormData();
  formData.append("type", type);
  if (type === "email") {
    formData.append("title", title);
    if (emailConfig?.paramSelections && Object.keys(emailConfig.paramSelections).length > 0) {
      formData.append("emailBodyTemplate", body);
      formData.append("emailParamSelections", JSON.stringify(emailConfig.paramSelections));
    } else {
      formData.append("body", buildEmailHtml(title, body));
    }
    attachments.forEach((file) => formData.append("attachments", file));
  } else {
    formData.append("templateName", whatsappConfig.templateName);
    formData.append("paramSelections", JSON.stringify(whatsappConfig.paramSelections));
  }
  const res = await fetch(`${API_URL}/api/courses/${courseId}/notify`, { method: "POST", body: formData });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to send notification");
  }
  return res.json();
};

const buildEmailHtml = (title, body) => {
  const htmlBody = body.replace(/\n/g, "<br>");
  return `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:40px 0;"><tr><td align="center"><table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.08);"><tr><td style="background:#0A0A0A;padding:24px 40px;text-align:center;"><img src="https://uraansoftskills.com/SkillVerse/images/Skillverse-Circle.png" alt="MySkillverse" height="40" style="display:block;margin:0 auto;"></td></tr><tr><td style="padding:40px;"><h1 style="margin:0 0 16px;font-size:22px;color:#0A0A0A;">${title}</h1><div style="font-size:15px;color:#444;line-height:1.7;">${htmlBody}</div></td></tr><tr><td style="padding:0 40px;"><hr style="border:none;border-top:1px solid #eee;"></td></tr><tr><td style="padding:24px 40px;text-align:center;"><p style="margin:0;font-size:12px;color:#999;">© 2026 MySkillverse · myskillverse.ai</p></td></tr></table></td></tr></table></body></html>`;
};

// The course brochure / fee structure sent to leads. kind: "brochure" | "feeStructure".
// Resolves to { courseBrochure, feeStructureBrochure } as stored after the change.
export const uploadCourseFile = async (id, kind, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_URL}/api/courses/${id}/files/${kind}`, { method: "POST", body: fd });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.message || "Upload failed");
  return json.data;
};

export const deleteCourseFile = async (id, kind) => {
  const res = await fetch(`${API_URL}/api/courses/${id}/files/${kind}`, { method: "DELETE" });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.message || "Could not remove the file");
  return json.data;
};
